<script setup>
import { useFeedbackEntry } from '@core/stores/feedback-entry'

import { cookieRef } from '@/@layouts/stores/config'
import CustomerFetchDialog from '@/views/customer/dialogs/CustomerFetchDialog.vue'
import AssortedFeedbackQuestion from '@/views/feedback/feedback-entry/tabs/AssortedFeedbackQuestion.vue'
import CustomerInfo from '@/views/feedback/feedback-entry/tabs/CustomerInfo.vue'
import FeedbackType from '@/views/feedback/feedback-entry/tabs/FeedbackType.vue'
import StandardFeedbackQuestion from '@/views/feedback/feedback-entry/tabs/StandardFeedbackQuestion.vue'

definePage({
  meta: {
    name: 'feedback-entry',
    subject: 'feedback.feedback',
    action: 'create',
  },  
})

const { t } = useI18n()

const route = useRoute('feedback-collection')
const feedbackId = ref(0)

const iconsSteps = [
  {
    title: 'Feedback Details',
  },
  {
    title: t('Customer')+' Info',
  },
  {
    title: 'Feedback Form',
  },
  {
    title: 'Review & Submit',
  },
]

const isCustomerDialogOpen = ref(false)

const refFeedbackTypeForm = ref()

const feedbackTypeFormData = ref({
  customerType: null,
  sourceType: null,
  formType: null,
})

useFeedbackEntry().fetchFormTypes()
useFeedbackEntry().fetchSourceTypes(route.params.type)

const refCustomerInfoForm = ref()

const customerInfoFormData = ref({
  name: '',
  phone: '',
  email: '',
  uhid: '',
  episode_no: '',
  location: '',
  customer_id: null,
  unit_id: null,
  episode_id: null,
  feedback_datetime: null,
  doctor_name: null,
})

const feedbackFormData = ref({})
const currentStep = ref(0)
const isCurrentStepValid = ref(true)


const validateFeedbackTypeForm = () => {
  refFeedbackTypeForm.value?.validate().then(valid => {
    if (valid.valid) {
      currentStep.value++
      isCurrentStepValid.value = true
      useFeedbackEntry().updateCurrentFeedback({
        customer_type: feedbackTypeFormData.value?.customerType,
        source_type: feedbackTypeFormData.value?.sourceType,
        form_type: feedbackTypeFormData.value?.formType,
        feedback_type: type.value,
        feedback: [],

      })
      nextTick(() => {
        refFeedbackTypeForm.value?.resetValidation()
        refFeedbackForm.value?.reset()
      })
    } else {
      isCurrentStepValid.value = false
    }
  })
}

const validateCustomerInfoForm = () => {
  refCustomerInfoForm.value?.resetValidation()

  if(customerInfoFormData.value?.customer_id !== null){
    updateCustomerInfoFormData()
  }
  refCustomerInfoForm.value?.validate().then(valid => {
    if (valid.valid) {
      currentStep.value++
      isCurrentStepValid.value = true
      nextTick(() => {
        refCustomerInfoForm.value?.resetValidation()
        updateCustomerInfoFormData()
      })
    } else {
      isCurrentStepValid.value = false
    }
  })
}

const refFeedbackForm = ref()

const updateCustomerInfoFormData =()=> {
  useFeedbackEntry().updateCurrentFeedback({
    customer: customerInfoFormData.value,
    unit_id: cookieRef('selectedUnit'),
    feedback: [],
  })
  refFeedbackForm.value?.reset()

}


const validateFeedbackForm = () => {
  refFeedbackForm.value?.validate().then(valid => {
    if (valid.valid) {
      currentStep.value++
      isCurrentStepValid.value = true
      useFeedbackEntry().updateCurrentFeedback({
        feedback: feedbackFormData.value?.filter(item => {
          if (item.response !== null && item.response !== '' && item.response !== undefined) {
            return item
          }
          
        }),
      })
      nextTick(() => {
        refFeedbackForm.value?.resetValidation()
      })
    } else {

      isCurrentStepValid.value = false
    }
  })
}

const resetFeedbackForm = () => {
  refFeedbackForm.value?.reset()
  useFeedbackEntry().updateCurrentFeedback({
    feedback: [],
  })
}

watch(feedbackFormData, () => {
  if (Array.isArray(feedbackFormData.value) && feedbackFormData.value.length > 0){
    useFeedbackEntry().updateCurrentFeedback({
      feedback: feedbackFormData.value?.filter(item => {
        if (item.response !== null && item.response !== '' && item.response !== undefined) {
          return item
        }
          
      }),
    })
  }
}, {
  immediate: true,
  deep: true,
})

const updateCurrentStep = value => {
  currentStep.value = value
}

const type =computed(() => route.params.type)

const breadcrumbs = computed(() => {
  return [
    { 
      title: 'Feedback Entry',
    },
    { 
      title: type.value === 'assorted' ? 'Assorted' : 'Standard',
      to: type.value === 'assorted' ? '/app/feedback/entry/assorted' : '/app/feedback/entry',
    },
    {
      title: 'Collect',
    },
  ]
})

const questionFeedbackComponent= computed(() => {
  return type.value==='assorted' ? AssortedFeedbackQuestion : StandardFeedbackQuestion
})

const submitLocation = ref(false)



const onSuccess = data => {
  //  reset customerInfoFormData and feedbackTypeFormData
  customerInfoFormData.value = {
    name: '',
    phone: '',
    email: '',
    uhid: '',
    episode_no: '',
    location: '',
    customer_id: null,
    episode_id: null,
  }

  feedbackTypeFormData.value = {
    customerType: null,
    sourceType: null,
    formType: null,
  }

  feedbackFormData.value = {}
    
  useFeedbackEntry().updateCurrentFeedback({
    customer: {
      customer_id: null,
      name: '',
      uhid: '',
      episode_no: '',
      email: '',
      phone: '',
      feedback_datetime: null,
      location: null,
    },
    unit_id: null,
    location_name: null,
    feedback_type: '',
    source_type: '',
    form_type: '',
    customer_type: '',
    feedback: [],
    attachment: null,
  })
 
  submitLocation.value = false
  feedbackId.value++
  currentStep.value = 0
}

const onSubmit = async data => {
  submitLocation.value = true
  await useFeedbackEntry().submitCurrentFeedback(false, onSuccess)
  submitLocation.value = false
}


const isCustomerApiConfigured = ref(false)


// check customer selection api available
await useApi(createUrl('/configurations/customer-api-status', {
  query: {
    unit_id: cookieRef('selectedUnit'),
  },
}), {
  afterFetch: response => {
    isCustomerApiConfigured.value = response?.data?.status
  },
})

onBeforeMount(() => {
  useFeedbackEntry().resetCurrentFeedback()
})

watch(feedbackTypeFormData, () => {
  if(feedbackTypeFormData.value?.customerType === 'PATIENT'){
    if (isCustomerApiConfigured.value &&(!isCustomerDialogOpen.value && customerInfoFormData.value?.customer_id === null)) {
      isCustomerDialogOpen.value = true
    }
  }else{
    customerInfoFormData.value = {
      name: '',
      phone: '',
      email: '',
      uhid: '',
      episode_no: '',
      location: '',
      customer_id: null,
      episode_id: null,
    }
  }
}, {
  deep: true,
  immediate: true,
})

// watch(customerInfoFormData, () => {
//   refCustomerInfoForm.value?.resetValidation()
 
// })


const getResponseValue=response=> {
  if (Array.isArray(response)) {
    return response.map(item => item?.label ?? item?.value ?? item).join(', ')
  }
  
  return response?.label ?? response?.value ?? response
}

const updateCustomerData = data => {
  customerInfoFormData.value = {
    ...customerInfoFormData.value,
    ...data,
  }
}

// * This FN validate & load data from url query
const loadPageQuery = async () => {
  if (
    route.query?.episode &&
    route.query?.source_type &&
    route.query?.form_type &&
    route.query?.location
  ) {
    // Customer
    const {
      data: episode,
    } = await useApi(createUrl(`/masters/episodes/${route.query?.episode}`))
    
    // Location
    let location
    if (episode.location) {
      location = episode.value.location
    } else {
      location = await useApi(createUrl(`/masters/locations/${route.query?.location}`))
      location = location.data
    }

    // Load data into forms
    customerInfoFormData.value = {
      ...episode.value.customer,
      customer_id: episode.value.customer.id,
      episode_id: episode.value.id,
      episode_no: episode.value.code,
      location: location.value,
    }

    feedbackTypeFormData.value = {
      customerType: 'PATIENT',
      sourceType: Number(route.query?.source_type),
      formType: Number(route.query?.form_type),
    }

    // Update state
    useFeedbackEntry().updateCurrentFeedback({
      customer_type: feedbackTypeFormData.value?.customerType,
      source_type: feedbackTypeFormData.value?.sourceType,
      form_type: feedbackTypeFormData.value?.formType,
      feedback_type: type.value,
      feedback: [],
    })
    useFeedbackEntry().updateCurrentFeedback({
      customer: customerInfoFormData.value,
      unit_id: cookieRef('selectedUnit'),
      feedback: [],
    })

    currentStep.value = 2
  }
}

onMounted(() => {
  loadPageQuery()
})
</script>

<template>
  <!-- 👉 Stepper -->
  <VBreadcrumbs :items="breadcrumbs" />
  <div class="mb-6">
    <AppStepper
      v-model:current-step="currentStep"
      :items="iconsSteps"
      :is-active-step-valid="isCurrentStepValid"
    />
  </div>

  <VCard>
    <!-- 👉 stepper content -->
    <VForm>
      <VWindow
        v-model="currentStep"
        :touch="false"
        class="disable-tab-transition"
      >
        <VWindowItem class="bg-var-theme-white">
          <VForm
            ref="refFeedbackTypeForm"
            @submit.prevent="validateFeedbackTypeForm"
          >
            <FeedbackType
              :data="feedbackTypeFormData"
              :current-step="currentStep"
              @update:current-step="updateCurrentStep"
              @update:feedback-type-form-data="feedbackTypeFormData = $event"
            />  
          </VForm>
        </VWindowItem>
        <VWindowItem>
          <VCardItem>
            <VForm
              ref="refCustomerInfoForm"
              @submit.prevent="validateCustomerInfoForm"
            >
              <CustomerInfo
                :data="customerInfoFormData"
                :current-step="currentStep"
                @update:customer-info-form-data="customerInfoFormData = $event"
                @update:current-step="updateCurrentStep"
              />  
            </VForm>
          </VCardItem>
        </VWindowItem>
        <VWindowItem>
          <VCardItem>
            <VForm
              ref="refFeedbackForm"
              @submit.prevent="validateFeedbackForm"
            >
              <Component
                :is="questionFeedbackComponent"
                :key="feedbackId"
                :current-step="currentStep"
                @update:current-step="updateCurrentStep"
                @update:feedback-type-form-data="feedbackFormData = $event"
                @update:reset-feedback-form="resetFeedbackForm"
              />
            </VForm>
          </VCardItem>
        </VWindowItem>
       
        <VWindowItem>
          <VCardItem>
            <VForm
              ref="refReviewAndSubmitForm"
              @submit.prevent="onSubmit"
            >
              <VCardText>
                <VRow>
                  <VCol cols="12 px-0">
                    <VCardTitle level="4">
                      Review & Submit <br>
                      <span class="text-body-2">Check details and responses before final submission</span>
                    </VCardTitle>
                  </VCol>
                  <VCol
                    cols="12"
                    class="px-0"
                  >
                    <VCardItem class="px-0">
                      <div class="text-base">
                        <div class="custom-background px-3 py-3 border-sm rounded-lg">
                          <h6 class="text-base font-weight-medium mb-2 text-primary">
                            Form Type
                          </h6>

                          <p class="d-flex align-center text-body-2 mb-1">
                            <span class="label">Form Type:</span>
                            <span class="font-weight-bold">{{ useFeedbackEntry().getFilledFeedback?.form_type?.title }}</span>
                          </p>
                         
                          <p class="d-flex text-body-2 align-center">
                            <span class="label">{{ $t('Customer') +' Type: ' }}</span>
                            <span class="font-weight-bold">{{ useFeedbackEntry().getFilledFeedback?.source_type?.title }}</span>
                          </p>
                        </div>

                        <div class="custom-background px-3 py-3 border-sm mt-4 rounded-lg">
                          <h6 class="text-base font-weight-medium mb-2 text-primary">
                            {{ $t('Customer') }} Info
                          </h6>
                          <p class="mb-1 text-body-2 d-flex align-center">
                            <span class="label">
                              Name :  
                            </span>
                            <span class="font-weight-bold">
                              {{ useFeedbackEntry().getFilledFeedback?.customer?.name }}
                            </span>
                          </p>
                          <p
                            v-if="useFeedbackEntry().getFilledFeedback?.customer?.uhid"
                            class="mb-1 text-body-2 d-flex align-center"
                          >
                            <span class="label">
                              UHID :  
                            </span>
                            <span class="font-weight-bold">
                              {{ useFeedbackEntry().getFilledFeedback?.customer?.uhid }}
                            </span>
                          </p>
                          <p
                            v-if="useFeedbackEntry().getFilledFeedback?.customer?.episode_no"
                            class="mb-1 text-body-2 d-flex align-center"
                          >
                            <span class="label">
                              Episode No :  
                            </span>
                            <span class="font-weight-bold">
                              {{ useFeedbackEntry().getFilledFeedback?.customer?.episode_no }}
                            </span>
                          </p>
                          <p class="mb-1 text-body-2 d-flex align-center">
                            <span class="label">
                              Mobile No :
                            </span>
                            <span class="font-weight-bold">
                              {{ useFeedbackEntry().getFilledFeedback?.customer?.phone }}
                            </span>
                          </p>
                          <p class="mb-1 text-body-2 d-flex align-center">
                            <span class="label">
                              Feedback Date :
                            </span>
                            <span class="font-weight-bold">
                              {{ useFeedbackEntry().getFilledFeedback?.customer?.feedback_datetime }}
                            </span>
                          </p>
                          <p class="mb-1 text-body-2 d-flex align-center">
                            <span class="label">
                              Location :
                            </span>
                            <span class="font-weight-bold">

                              {{ useFeedbackEntry().getFilledFeedback?.customer?.location?.name }}
                            </span>
                          </p>
                        </div>
                        <div class="px-3 py-3 border-sm mt-4 rounded-lg">
                          <h6 class="text-base font-weight-medium text-primary">
                            Feedback Response
                          </h6>
                          <VDivider class="mt-2 mb-3" />
                          <div
                            v-for="(item, index) in useFeedbackEntry().getFilledFeedback?.feedback"
                            :key="index"
                          >
                            <p class="mb-2 text-body-1 font-weight-medium mt-1">
                              <span class="me-3 custom-background pa-1 rounded text-primary ">Q.{{ index + 1 }}  </span> {{ item.question_label }}
                            </p>
                            <p class="mb-4 text-body-1 px-1 py-3 rounded-lg custom-background text-high-emphasis border-sm">
                              <span class="ms-1 me-2"> Ans:</span> {{ getResponseValue(item.response) }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </VCardItem>
                  </VCol>
                </VRow>
                <VCol cols="12">
                  <div class="d-flex flex-wrap gap-4 justify-space-between mt-8">
                    <VBtn
                      color="secondary"
                      variant="tonal"
                      :disabled="currentStep === 0"
                      @click="currentStep--"
                    >
                      <VIcon
                        icon="tabler-arrow-left"
                        start
                        class="flip-in-rtl"
                      />
                      Previous
                    </VBtn>
        
                    <VBtn
                      color="success"
                      type="submit"
                      :loading="submitLocation"
                    >
                      submit
                    </VBtn>
                  </div>
                </VCol>
              </VCardText>
            </VForm>
          </VCardItem>
        </VWindowItem>
      </VWindow>
    </VForm>
  </VCard>
  <CustomerFetchDialog
    v-model:isDialogOpen="isCustomerDialogOpen"
    @update:customer-dialog-data="updateCustomerData"
    @update:clear-customer-selection="feedbackTypeFormData.customerType = ''"
  />
</template>

<style scoped>
.label {
  inline-size: 150px;
}
</style>

<style lang="scss">
.custom-background{
  background-color: rgba(0, 102, 255, 5%);
}
</style>
